import type {CreatedGroup, UUID} from "@/types/common";
import type {ToastHandle, ToastService} from "@/types/toast";
import type {TranslatedText, TranslationEditor, TranslationProperty, SpecialFieldType, ExtractedTranslation, DynamicTranslationUpdate, TranslationFieldUpdate, TranslationObjectUpdate, StaticTranslation, FullTranslationGroup} from "@/types/translations";
import {v4 as uuidv4} from "uuid";
import {getNextBaseText} from "@/utils/FormatUtils";

export function defaultTranslationItem(): TranslatedText[] {
    return [
        {language: 'en', text: ''},
        {language: 'pl', text: ''},
        {language: 'tr', text: ''},
        {language: 'de', text: ''},
        {language: 'es', text: ''},
        {language: 'br', text: ''},
        {language: 'fr', text: ''},
        {language: 'it', text: ''},
        {language: 'jp', text: ''},
        {language: 'cn', text: ''},
        {language: 'ar', text: ''},
        {language: 'ru', text: ''},
        {language: 'uk', text: ''},
        // {language: 'se', text: ''},
        // {language: 'ro', text: ''},
        // {language: 'gr', text: ''},
    ]
}

function fillDefaultTranslation(translatedTexts: TranslatedText[]) {
    const defaultTexts = defaultTranslationItem();
    const filled = [...defaultTexts];

    for (const dt of defaultTexts) {
        const existing = translatedTexts.find((tt: { language: string; }) => tt.language === dt.language);
        if (existing) {
            dt.text = existing.text;
        }
    }

    return filled;
}

export function translationItemObject<K extends string>(translationId: UUID, name: K, translatedTexts: TranslatedText[] = defaultTranslationItem()): TranslationProperty<K> {
    if (!translationId || !name) {
        throw new Error('translationId and name are required');
    }

    return {
        [`${name}_translation`]: {
            fuid: translationId,
            translated_texts: [
                ...(translatedTexts),
            ]
        }
    } as TranslationProperty<K>;
}

export function generateTranslation(copy: boolean, originalTranslation: TranslationEditor, fieldType: SpecialFieldType | null = null): TranslationEditor {
    if (!copy) {
        return originalTranslation;
    }

    return {
        fuid: uuidv4(),
        translated_texts: originalTranslation.translated_texts,
        ...(fieldType ? {special_field_type: fieldType} : {}),
    };
}

/**
 * Extracts all translation objects (with `fuid` and `translated_texts`)
 * from a single object or an array of objects.
 * Adds `item_id` to each translation using a customizable id extractor.
 *
 * @param {Object|Object[]} input - A single object or an array of objects (e.g. this.item or this.items)
 * @param {Function} idExtractor - Function to extract item ID (default: item => item.id)
 * @returns {Array} - Array of translation objects with `item_id`, `fuid`, and `translated_texts`
 */
export function extractTranslations<T>(input: T | T[], idExtractor: (item: T) => UUID = item => (item as {id: UUID}).id): ExtractedTranslation[] {
    const translations: ExtractedTranslation[] = [];

    const list = Array.isArray(input) ? input : [input];

    for (const item of list) {
        const itemId = idExtractor(item);

        function traverse(value: unknown) {
            if (Array.isArray(value)) {
                value.forEach(traverse);
            } else if (value && typeof value === 'object') {
                if ('fuid' in value) { // && 'translated_texts' in value
                    translations.push({
                        id: itemId,
                        fuid: (value as TranslationEditor).fuid,
                        translated_texts: (value as TranslationEditor).translated_texts,
                        ...((value as TranslationEditor).special_field_type ? {special_field_type: (value as TranslationEditor).special_field_type} : {}),
                        ...((value as TranslationEditor).optional ? {optional: (value as TranslationEditor).optional} : {})
                    });
                }

                Object.values(value).forEach(traverse);
            }
        }

        traverse(item);
    }

    return translations;
}

export function getDefaultTranslationText(input: unknown, specialFieldType: SpecialFieldType) {
    const translations = extractTranslations(input);

    const filtered = translations.filter(t => t.special_field_type === specialFieldType);

    if (!filtered || filtered.length === 0) {
        return Error(`No translations found for special field type: ${specialFieldType}`);
    }

    for (const item of filtered) {
        const enText = item.translated_texts.find((t: { language: string; }) => t.language === 'en');
        if (enText?.text) {
            return enText.text;
        }
    }

    return '';
}

export function extractTranslationsUids(input: unknown) {
    const translationsUids: UUID[] = [];

    const list = Array.isArray(input) ? input : [input];

    for (const item of list) {
        function traverse(value: unknown) {
            if (Array.isArray(value)) {
                value.forEach(traverse);
            } else if (value && typeof value === 'object') {
                if ('fuid' in value) {
                    translationsUids.push((value as TranslationEditor).fuid);
                }

                Object.values(value).forEach(traverse);
            }
        }

        traverse(item);
    }

    return {objects: translationsUids};
}

/**
 * Updates the `object_id` field in each translation object
 * with a new UID.
 *
 * @param translationPayload - The payload containing translations to update.
 * @param groups - Array of groups, each containing objects with `object_id` and `group_id`.
 * @param toast - Optional toast instance for error notifications.
 * @returns {{translations: *}|Error} - Updated translation payload with new UIDs or an error if the lengths do not match.
 */

export function updateTranslationsIds(translationPayload: DynamicTranslationUpdate, groups: CreatedGroup[], toast: (ToastHandle & Partial<Pick<ToastService, "update">>) | null = null) {
    const translations = translationPayload.translations;

    const objectToGroupsMap: Record<UUID, UUID[]> = {};

    groups.forEach(group => {
        group.objects.forEach((object_id: string|number) => {
            if (!objectToGroupsMap[object_id]) {
                objectToGroupsMap[object_id] = [];
            }
            objectToGroupsMap[object_id].push(group.group_id);
        });
    });

    const objectIdsFromGroups = Object.keys(objectToGroupsMap);
    const expectedCount = objectIdsFromGroups.length;

    if (expectedCount !== translations.length) {
        if (toast) {
            toast.update!(toast.id, {
                description: "Mismatch: The number of unique objects in groups does not match the number of translations.",
                type: 'error'
            });
        }
        console.error("Mismatch between group objects and translations.");
        return {};
    }

    const updatedTranslations = objectIdsFromGroups.map((object_id, index) => {
        const groups_ids = objectToGroupsMap[object_id];
        return {
            fields_translations: translations[index].fields_translations,
            object_id,
            groups_ids
        };
    });

    return {translations: updatedTranslations};
}

/**
 * Maps extracted translations into a payload suitable for dynamic translation update.
 *
 * @param {Array} translations - Array of translation objects with `id`, `fuid`, `translated_texts`
 * @param {String|Array<String>} [groupId] - Single group ID or array of group IDs
 * @param {Boolean} [copy=false] - Whether to prefix non-empty texts with "copy_of "
 * @returns {Object} - Payload object with `translations` field
 */
export function mapDynamicTranslationUpdate(translations: ExtractedTranslation[], groupId?: UUID | UUID[] | null, copy = false): DynamicTranslationUpdate {
    const grouped: Record<UUID, TranslationFieldUpdate[]> = {};

    for (const t of translations) {
        if (!grouped[t.id]) {
            grouped[t.id] = [];
        }

        const hasTranslatedTexts = Array.isArray(t.translated_texts) && t.translated_texts.length > 0;

        let translatedTexts: TranslatedText[] = [];

        if (hasTranslatedTexts) {
            const shouldClearAllTexts =
                t.optional === true &&
                !t.translated_texts.some((tt: { language: string; text: string; }) => tt.language === 'en' && tt.text?.trim());

            translatedTexts = t.translated_texts.map(tt => {
                let text = shouldClearAllTexts ? '' : tt.text?.trim() || '';
                if (copy && text) {
                    text = getNextBaseText(text);
                }

                return {
                    language: tt.language,
                    text
                };
            }).filter((tt: { text: string; }) => tt.text !== '' || copy || t.optional === true);
        }

        const fieldTranslation = {
            field_translation_id: t.fuid,
            ...(translatedTexts.length > 0 ? {translated_texts: translatedTexts} : {}),
            ...(t.special_field_type ? {special_field_type: t.special_field_type} : {})
        };

        grouped[t.id].push(fieldTranslation);
    }

    const ids = Object.keys(grouped);

    return {
        translations: ids.map((id, index) => {
            const translationObj: TranslationObjectUpdate = {
                object_id: id,
                fields_translations: grouped[id]
            };

            if (typeof groupId === 'string' && groupId.trim()) {
                translationObj.group_id = groupId;
            } else if (Array.isArray(groupId) && groupId[index]) {
                translationObj.group_id = groupId[index];
            }

            return translationObj;
        })
    };
}

export function mapStaticTranslationUpdate(key: string, translations: TranslatedText[], ai_instructions: string, active: boolean) {
    const filteredTranslations = translations.filter((t: { text: string; }) => {
        return !(!t.text || t.text.trim() === '');
    });

    return {
        key,
        active: active,
        ai_instructions: ai_instructions,
        category: 'game',
        translated_texts: filteredTranslations.map(t => ({
            language: t.language,
            text: t.text
        }))
    };
}

export function mapBulkStaticTranslations(sourceList: StaticTranslation[], currentList: StaticTranslation[]) {
    const sourceMap = new Map(
        sourceList.map(item => [item.key, item])
    );

    return currentList
        .filter(item => hasItemChanged(sourceMap.get(item.key), item))
        .map(item => ({
            active: item.active,
            ai_instructions: item.ai_instructions,
            context: item.context,
            source: item.source,
            key: item.key,
            category: item.category,
            translated_texts: normalizeTranslations(item.translated_text)
        }));
}

function normalizeTranslations(translations: TranslatedText[] = []) {
    return translations
        .filter(t => t.text && t.text.trim() !== '')
        .map(t => ({
            language: t.language,
            text: t.text
        }))
        .sort((a, b) => a.language.localeCompare(b.language));
}

export function hasItemChanged(source: StaticTranslation | undefined, current: StaticTranslation) {
    if (!source) return true;

    if (source.active !== current.active) return true;
    if (source.ai_instructions !== current.ai_instructions) return true;
    if (source.context !== current.context) return true;
    if (source.source !== current.source) return true;
    if (source.category !== current.category) return true;

    const sourceTranslations = normalizeTranslations(source.translated_text);
    const currentTranslations = normalizeTranslations(current.translated_text);

    return JSON.stringify(sourceTranslations) !== JSON.stringify(currentTranslations);
}

export function normalizeStaticTranslationData(item: Partial<StaticTranslation>): StaticTranslation {
    const defaults = defaultTranslationItem();

    return {
        active: item.active || false,
        ai_instructions: item.ai_instructions || '',
        context: item.context || '',
        source: item.source || '',

        category: item.category || '',
        key: item.key || '',
        translated_text: defaults.map(defaultItem => {
            const existing = item.translated_text?.find(
(                t: { language: string; }) => t.language === defaultItem.language
            );

            if (existing) {
                return {
                    language: existing.language,
                    text: existing.is_fallback ? '' : existing.text,
                    is_fallback: !!existing.is_fallback,
                };
            }

            return {
                language: defaultItem.language,
                text: '',
                is_fallback: true,
            };
        }),
    };
}

export function normalizeDynamicTranslationData(data: FullTranslationGroup[]) {
    return data.map(group => ({
        group: {
            id: group.group.id,
            name: group.group.name,
            pos: group.group.pos
        },
        children: (group.objects || []).map(child => ({
            id: child.object_id,
            normalized_id: child.normalized_id,
            pos: child.pos,

            fields_translations: (child.fields_translations || []).map(ft => ({
                field_translation_id: ft.field_translation_id,
                field_name: ft.field_name,
                special_field_type: ft.special_field_type,
                translated_texts: fillDefaultTranslation(ft.translated_texts)
            }))
        }))
    }))
}
